import FormattedMessage from 'I18n/components/FormattedMessage';
import styled, { css } from 'styled-components';
import SVGKnowledgeBase from 'visitor-ui-component-library-icons/icons/SVGKnowledgeBase';
import SVGWebsite from 'visitor-ui-component-library-icons/icons/SVGWebsite';
import { OBSIDIAN, WHITE } from 'visitor-ui-component-library/constants/WidgetColors';
import { useWidgetModalOrContext } from '../widget-modal/WidgetModalContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TEXT_COLOR = '#141414'; // trellis/color/neutral/1600
const BORDER_RADIUS = 4;
const iconBaseStyles = css(["flex-shrink:0;fill:", ";font-size:16px;margin-right:4px;width:16px;height:16px;"], TEXT_COLOR);
const WebsiteIcon = styled(SVGWebsite).withConfig({
  displayName: "SourcesModalContent__WebsiteIcon"
})(["", ""], iconBaseStyles);
const KnowledgeBaseIcon = styled(SVGKnowledgeBase).withConfig({
  displayName: "SourcesModalContent__KnowledgeBaseIcon"
})(["", ""], iconBaseStyles);
const flexAlignCenterStyles = css(["display:flex;align-items:center;"]);
const SourcesHeading = styled.div.withConfig({
  displayName: "SourcesModalContent__SourcesHeading"
})(["", " font-size:16px;line-height:20px;font-weight:600;color:", ";"], flexAlignCenterStyles, OBSIDIAN);
const SourcesContent = styled.div.withConfig({
  displayName: "SourcesModalContent__SourcesContent"
})(["display:flex;flex-direction:column;gap:8px;margin-top:12px;"]);
const sourcesBaseStyles = css(["color:", ";font-size:12px;line-height:18px;font-weight:600;word-break:break-word;"], TEXT_COLOR);
const sourcesLinkStyles = css(["padding:8px 12px 8px 6px;&:hover{text-decoration:underline;}"]);
const SourcesType = styled.div.withConfig({
  displayName: "SourcesModalContent__SourcesType"
})(["", " border:1px solid #ccc;border-radius:", "px;padding:8px 12px;"], sourcesBaseStyles, BORDER_RADIUS);
const SourcesTypeHeader = styled.div.withConfig({
  displayName: "SourcesModalContent__SourcesTypeHeader"
})(["", " margin:4px 0;"], flexAlignCenterStyles);
const StyledFormattedMessage = styled(FormattedMessage).withConfig({
  displayName: "SourcesModalContent__StyledFormattedMessage"
})(["display:block;"]);
const SourcesLink = styled.a.withConfig({
  displayName: "SourcesModalContent__SourcesLink"
})(["", " ", " ", " color:", ";text-decoration:none;"], sourcesBaseStyles, sourcesLinkStyles, flexAlignCenterStyles, ({
  $accentColor
}) => $accentColor);
const SourcesButton = styled.button.withConfig({
  displayName: "SourcesModalContent__SourcesButton"
})(["", " ", " ", " color:", ";text-decoration:none;border:none;background-color:transparent;cursor:pointer;"], sourcesBaseStyles, sourcesLinkStyles, flexAlignCenterStyles, ({
  $accentColor
}) => $accentColor);
const SourceIndex = styled.span.withConfig({
  displayName: "SourcesModalContent__SourceIndex"
})(["display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;margin-left:10px;border-radius:50%;aspect-ratio:1/1;text-decoration:none;font-size:12px;background-color:", ";color:", ";"], TEXT_COLOR, WHITE);
const SourcesRowWrapper = styled.div.withConfig({
  displayName: "SourcesModalContent__SourcesRowWrapper"
})(["", ""], flexAlignCenterStyles);
const SourcesButtonAsLink = ({
  accentColor,
  children,
  sources,
  browserWindowHeight,
  navigateToKnowledgeBaseArticle
}) => {
  const {
    closeModal
  } = useWidgetModalOrContext();
  const handleClick = () => {
    sources.forEach(source => {
      navigateToKnowledgeBaseArticle({
        deepLink: source.deepLink,
        hubSpotContentId: source.hubSpotContentId,
        hubSpotContentTextFragment: source.hubSpotContentTextFragment
      }, browserWindowHeight);
    });
    closeModal();
  };
  return /*#__PURE__*/_jsx(SourcesButton, {
    $accentColor: accentColor,
    onClick: handleClick,
    children: children || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "conversations-visitor-message-history.viewSource"
    })
  });
};
SourcesButtonAsLink.displayName = 'SourcesButtonAsLink';
export const SourcesModalContent = ({
  accentColor,
  browserWindowHeight,
  navigateToKnowledgeBaseArticle,
  sources
}) => {
  const websiteSources = [];
  const knowledgeBaseSources = [];
  sources.forEach((source, index) => {
    if (source.hubSpotContentType === 'KB_ARTICLE') {
      knowledgeBaseSources.push(Object.assign({}, source, {
        index
      }));
    } else {
      websiteSources.push(Object.assign({}, source, {
        index
      }));
    }
  });
  return {
    headerContent: /*#__PURE__*/_jsx(SourcesHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-visitor-message-history.sources"
      })
    }),
    bodyContent: /*#__PURE__*/_jsxs(SourcesContent, {
      children: [websiteSources.length > 0 && /*#__PURE__*/_jsxs(SourcesType, {
        children: [/*#__PURE__*/_jsxs(SourcesTypeHeader, {
          children: [/*#__PURE__*/_jsx(WebsiteIcon, {}), /*#__PURE__*/_jsx(StyledFormattedMessage, {
            message: "conversations-visitor-message-history.website.plural"
          })]
        }), websiteSources.map(source => {
          return /*#__PURE__*/_jsxs(SourcesRowWrapper, {
            children: [/*#__PURE__*/_jsx(SourceIndex, {
              children: source.index + 1
            }), /*#__PURE__*/_jsx(SourcesLink, {
              $accentColor: accentColor,
              href: source.deepLink,
              target: "_blank",
              children: source.url
            }, source.citationId)]
          }, source.citationId);
        })]
      }, "websites"), knowledgeBaseSources.length > 0 && /*#__PURE__*/_jsxs(SourcesType, {
        children: [/*#__PURE__*/_jsxs(SourcesTypeHeader, {
          children: [/*#__PURE__*/_jsx(KnowledgeBaseIcon, {}), /*#__PURE__*/_jsx(StyledFormattedMessage, {
            message: "conversations-visitor-message-history.knowledgeBase"
          })]
        }), knowledgeBaseSources.map(source => {
          return /*#__PURE__*/_jsxs(SourcesRowWrapper, {
            children: [/*#__PURE__*/_jsx(SourceIndex, {
              children: source.index + 1
            }), /*#__PURE__*/_jsx(SourcesButtonAsLink, {
              accentColor: accentColor,
              browserWindowHeight: browserWindowHeight,
              navigateToKnowledgeBaseArticle: navigateToKnowledgeBaseArticle,
              sources: [source],
              children: source.title
            }, source.citationId)]
          }, source.citationId);
        })]
      }, "knowledge-base")]
    })
  };
};
SourcesModalContent.displayName = 'SourcesModalContent';